import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import {
  uploadConnectorService,
  UploadProduct,
  UploadConnector,
} from '../services/uploadConnector';
import { useToast } from '../hooks/use-toast';
import {
  Package,
  Search,
  Loader2,
  Images,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  Grid3X3,
  List,
  RefreshCw,
  FileImage,
  CheckCircle2,
  MoreHorizontal,
  Palette,
  FileEdit,
  Check,
  DollarSign,
  Tag,
  ShoppingCart,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, formatDistanceToNow } from 'date-fns';
import { useOrganization } from '../contexts/OrganizationContext';

interface ProductFilters {
  status?: string;
  search?: string;
  page: number;
  limit: number;
}

const formatTimeAgo = (dateStr: string): string => {
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
};

// Product Card Component
const UploadProductCard: React.FC<{
  product: UploadProduct;
  onView: (product: UploadProduct) => void;
  onDelete: (product: UploadProduct) => void;
  isSelected?: boolean;
  onSelect?: (product: UploadProduct) => void;
  isSelectable?: boolean;
  amazonEnabled?: boolean;
}> = ({ product, onView, onDelete, isSelected, onSelect, isSelectable, amazonEnabled }) => {
  const name = product.name_en || product.name_ar || 'Untitled Product';
  const hasImages = (product.image_count || 0) > 0;

  return (
    <Card className={`card-modern-enhanced group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 flex flex-col h-full overflow-hidden relative ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      {/* Product image */}
      <div className="relative aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-t-xl overflow-hidden">
        {/* Selection checkbox */}
        {isSelectable && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect?.(product); }}
            className={`absolute top-3 left-3 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
              isSelected
                ? 'bg-primary border-primary text-primary-foreground'
                : 'bg-black/50 border-white/60 text-transparent hover:border-white'
            }`}
          >
            {isSelected && <Check className="h-4 w-4" />}
          </button>
        )}
        {product.cover_image ? (
          <img
            src={product.cover_image}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <FileImage className="h-16 w-16 group-hover:scale-110 transition-transform duration-300" />
          </div>
        )}

        {/* Modern overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />

        {/* Shimmer effect on hover */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -skew-x-12 -translate-x-full group-hover:translate-x-full"
          style={{ transition: 'transform 0.7s ease-out, opacity 0.3s' }}
        />

        {/* Image count badge */}
        {hasImages && (
          <div className="absolute top-3 right-3 bg-black/90 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center font-medium shadow-xl border border-white/10 group-hover:scale-110 transition-transform duration-300">
            <Images className="h-3 w-3 mr-1.5" />
            {product.image_count}
          </div>
        )}

        {/* Status badge - only show when complete */}
        {product.status === 'complete' && (
          <div className="absolute bottom-3 right-3">
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-xl border border-white/20">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Complete
            </span>
          </div>
        )}

        {/* No images overlay */}
        {!hasImages && (
          <div className="absolute inset-0 bg-gradient-to-br from-muted-foreground/80 to-muted-foreground/60 flex items-center justify-center backdrop-blur-sm">
            <div className="text-center text-white px-4 group-hover:scale-105 transition-transform duration-300">
              <FileImage className="h-12 w-12 mx-auto mb-3 opacity-80 group-hover:animate-bounce" />
              <div className="text-sm font-medium">No images</div>
              <div className="text-xs opacity-80 mt-1">available</div>
            </div>
          </div>
        )}

        {/* Actions Menu */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 bg-black/70 border border-white/20 text-white hover:bg-black/90"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(product)}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(product)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CardContent className="p-6 flex flex-col flex-1">
        <div className="flex-1 space-y-4">
          {/* Product info */}
          <div className="space-y-2">
            <CardTitle className="text-lg line-clamp-2 font-semibold leading-tight">
              {name}
            </CardTitle>
            {product.barcode && (
              <CardDescription className="text-sm font-medium text-muted-foreground font-mono">
                {product.barcode}
              </CardDescription>
            )}
            {(product.sku || product.price != null) && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {product.sku && (
                  <span className="flex items-center gap-1 font-mono">
                    <Tag className="h-3 w-3" />
                    {product.sku}
                  </span>
                )}
                {product.price != null && (
                  <span className="flex items-center gap-0.5 font-medium">
                    <DollarSign className="h-3 w-3" />
                    {Number(product.price).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
            <div className="flex items-center gap-1">
              <Images className="h-3 w-3" />
              <span>{product.image_count || 0} images</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(product.created_at), 'MMM d')}</span>
            </div>
          </div>

          {/* Approval Status */}
          <div className="flex items-center gap-2 pt-2">
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
              product.media_approved
                ? 'bg-green-500/10 text-green-600'
                : 'bg-muted text-muted-foreground'
            }`}>
              {product.media_approved ? <Check className="h-3 w-3" /> : <Palette className="h-3 w-3" />}
              <span>Media</span>
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
              product.content_approved
                ? 'bg-green-500/10 text-green-600'
                : 'bg-muted text-muted-foreground'
            }`}>
              {product.content_approved ? <Check className="h-3 w-3" /> : <FileEdit className="h-3 w-3" />}
              <span>Content</span>
            </div>
          </div>
          {amazonEnabled && product.amazon_export && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-amber-500/10 text-amber-600 w-fit">
              <ShoppingCart className="h-3 w-3 shrink-0" />
              <span>Exported {formatTimeAgo(product.amazon_export.last_exported_at)}</span>
            </div>
          )}
        </div>

        {/* Actions - always at bottom */}
        <div className="flex gap-2 pt-4 mt-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 hover:bg-muted/50 hover:scale-105 hover:-translate-y-0.5 transition-all duration-200"
            onClick={() => onView(product)}
          >
            <Eye className="h-3 w-3 mr-1.5" />
            View
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="hover:scale-105 hover:-translate-y-0.5 transition-all duration-200"
            onClick={() => onDelete(product)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Product List Item Component (for list view)
const UploadProductListItem: React.FC<{
  product: UploadProduct;
  onView: (product: UploadProduct) => void;
  onDelete: (product: UploadProduct) => void;
  isSelected?: boolean;
  onSelect?: (product: UploadProduct) => void;
  isSelectable?: boolean;
}> = ({ product, onView, onDelete, isSelected, onSelect, isSelectable }) => {
  const name = product.name_en || product.name_ar || 'Untitled Product';

  return (
    <Card className={`card-modern group hover:shadow-lg transition-all duration-300 ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Selection checkbox */}
          {isSelectable && (
            <button
              onClick={(e) => { e.stopPropagation(); onSelect?.(product); }}
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
                isSelected
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-muted-foreground/40 hover:border-primary'
              }`}
            >
              {isSelected && <Check className="h-4 w-4" />}
            </button>
          )}

          {/* Thumbnail */}
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
            {product.cover_image ? (
              <img
                src={product.cover_image}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FileImage className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-semibold text-base truncate">{name}</h3>
                {product.barcode && (
                  <p className="text-sm text-muted-foreground font-mono">{product.barcode}</p>
                )}
                {(product.sku || product.price != null) && (
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {product.sku && (
                      <span className="flex items-center gap-1 font-mono">
                        <Tag className="h-3 w-3" />
                        {product.sku}
                      </span>
                    )}
                    {product.price != null && (
                      <span className="flex items-center gap-0.5 font-medium">
                        <DollarSign className="h-3 w-3" />
                        {Number(product.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Approval Status */}
              <div className="flex items-center gap-2 shrink-0">
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                  product.media_approved
                    ? 'bg-green-500/10 text-green-600'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {product.media_approved ? <Check className="h-3 w-3" /> : <Palette className="h-3 w-3" />}
                  <span>Media</span>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                  product.content_approved
                    ? 'bg-green-500/10 text-green-600'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {product.content_approved ? <Check className="h-3 w-3" /> : <FileEdit className="h-3 w-3" />}
                  <span>Content</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Images className="h-4 w-4" />
                <span>{product.image_count || 0} images</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(product.created_at), 'MMM d, yyyy')}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => onView(product)}>
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(product)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const UploadProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const amazonEnabled = !!currentOrganization?.settings?.amazon_enabled;
  const contentRef = useRef<HTMLDivElement>(null);

  const [connector, setConnector] = useState<UploadConnector | null>(null);
  const [products, setProducts] = useState<UploadProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Parse filters from URL
  const getFiltersFromURL = (): ProductFilters => ({
    status: searchParams.get('status') || undefined,
    search: searchParams.get('search') || undefined,
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '20'),
  });

  const [filters, setFilters] = useState<ProductFilters>(getFiltersFromURL());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Selection state for Amazon export
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  // Delete state
  const [productToDelete, setProductToDelete] = useState<UploadProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Update URL with current filters
  const updateURL = (newFilters: ProductFilters) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });
    setSearchParams(params);
  };

  // Fetch connector
  useEffect(() => {
    const fetchConnector = async () => {
      try {
        const conn = await uploadConnectorService.getDefaultConnector();
        setConnector(conn);
      } catch (err) {
        console.error('Failed to fetch connector:', err);
      }
    };
    fetchConnector();
  }, []);

  // Update filters when URL changes
  useEffect(() => {
    setFilters(getFiltersFromURL());
  }, [searchParams]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (!connector) return;

    setIsLoading(true);
    try {
      const result = await uploadConnectorService.getProducts(
        connector.id,
        filters.page,
        filters.limit,
        filters.status !== 'all' ? filters.status : undefined
      );
      setProducts(result.products);
      setPagination({
        page: result.pagination.page,
        limit: result.pagination.limit,
        total: result.pagination.total,
        pages: result.pagination.totalPages,
      });
    } catch (err) {
      console.error('Failed to fetch products:', err);
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [connector, filters.page, filters.limit, filters.status, toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Filter products by search (client-side for now)
  const filteredProducts = products.filter((product) => {
    if (!filters.search) return true;
    const name = product.name_en || product.name_ar || '';
    const barcode = product.barcode || '';
    const searchLower = filters.search.toLowerCase();
    return name.toLowerCase().includes(searchLower) || barcode.toLowerCase().includes(searchLower);
  });

  const handleFiltersChange = (key: keyof ProductFilters, value: string | undefined) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    updateURL(newFilters);
  };

  const handlePageChange = (page: number) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    updateURL(newFilters);
    // Scroll to top
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleViewProduct = (product: UploadProduct) => {
    navigate(`/upload-products/${product.id}`);
  };

  const handleDelete = async () => {
    if (!connector || !productToDelete) return;

    setIsDeleting(true);
    try {
      await uploadConnectorService.deleteProduct(connector.id, productToDelete.id);
      toast({
        title: 'Product deleted',
        description: 'The product has been deleted successfully.',
      });
      setProductToDelete(null);
      fetchProducts();
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Selection helpers
  const isProductSelectable = (product: UploadProduct) =>
    product.media_approved && product.content_approved;

  const handleToggleSelect = (product: UploadProduct) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.add(product.id);
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedProductIds(new Set());

  const handleExportForAmazon = () => {
    const ids = Array.from(selectedProductIds).join(',');
    navigate(`/amazon-export?products=${ids}`);
  };

  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div ref={contentRef} className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-down">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-muted-foreground">Manage your grouped image products</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchProducts} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {/* View Toggle */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 border">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="px-3 h-9"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="px-3 h-9"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-modern">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or barcode..."
                value={filters.search || ''}
                onChange={(e) => handleFiltersChange('search', e.target.value || undefined)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFiltersChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft (No Approvals)</SelectItem>
                <SelectItem value="partial">Partial Approval</SelectItem>
                <SelectItem value="complete">Complete (Both Approved)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products List */}
      {isLoading ? (
        <div className="space-y-6">
          {/* Loading header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Loading products...</span>
            </div>
          </div>

          {/* Skeleton grid/list */}
          <div className="animate-pulse">
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array(8)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="h-80 bg-muted rounded-xl"></div>
                  ))}
              </div>
            ) : (
              <div className="space-y-4">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="h-28 bg-muted rounded-xl"></div>
                  ))}
              </div>
            )}
          </div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card className="card-modern">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-muted/50 rounded-full mb-6">
              <Package className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl mb-3">No Products Found</CardTitle>
            <CardDescription className="text-center mb-8 max-w-md">
              {filters.search || filters.status
                ? 'No products match your current filters. Try adjusting your search criteria.'
                : 'Create products by selecting images from the Assets page.'}
            </CardDescription>
            {!filters.search && !filters.status && (
              <Button onClick={() => navigate('/assets')}>Go to Assets</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Results summary */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-muted-foreground">
              Showing <span className="text-foreground">{startItem}-{endItem}</span> of{' '}
              <span className="text-foreground">{pagination.total}</span> products
            </div>
          </div>

          {/* Products display */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <UploadProductCard
                    product={product}
                    onView={handleViewProduct}
                    onDelete={setProductToDelete}
                    isSelected={selectedProductIds.has(product.id)}
                    onSelect={handleToggleSelect}
                    isSelectable={isProductSelectable(product)}
                    amazonEnabled={amazonEnabled}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <UploadProductListItem
                    product={product}
                    onView={handleViewProduct}
                    onDelete={setProductToDelete}
                    isSelected={selectedProductIds.has(product.id)}
                    onSelect={handleToggleSelect}
                    isSelectable={isProductSelectable(product)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Card className="card-modern">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page <span className="font-medium text-foreground">{pagination.page}</span> of{' '}
                    <span className="font-medium text-foreground">{pagination.pages}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1 || isLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === pagination.page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isLoading}
                            className="w-10 h-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages || isLoading}
                      className="gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Selection Action Bar */}
      {selectedProductIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <Card className="shadow-2xl border-primary/20 bg-background/95 backdrop-blur-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedProductIds.size} product{selectedProductIds.size !== 1 ? 's' : ''} selected
              </span>
              {amazonEnabled && (
                <Button
                  onClick={handleExportForAmazon}
                  className="gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Export for Amazon
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearSelection}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!productToDelete}
        onOpenChange={(open) => !open && setProductToDelete(null)}
        title="Delete Product"
        description={`Are you sure you want to delete "${
          productToDelete?.name_en || productToDelete?.name_ar || 'this product'
        }"? The images will be unlinked but not deleted.`}
        confirmText="Delete"
        loadingText="Deleting..."
        onConfirm={handleDelete}
        variant="destructive"
        isLoading={isDeleting}
      />
    </div>
  );
};
